/* eslint @typescript-eslint/no-explicit-any: 0  */
import React from 'react';

// good for mapping between enums and readable strings
type EnumMap<T extends ReadonlyArray<string>, U = string> = Readonly<
    Record<T[number], U>
>;

type GenericFunction<R = any> = (...args: any[]) => R;

type GenericAsyncFunction = (...args: any[]) => PromiseLike<any>;

type MouseHandler = React.MouseEventHandler<HTMLElement>;

type PromiseType<T> = T extends PromiseLike<infer X> ? X : never;

type FullyDefinedObj<T> = {
    [K in keyof T]-?: Exclude<T[K], undefined>;
};

type PromiseLikeUnwrap<T> = T extends PromiseLike<infer U>
    ? PromiseLikeUnwrap<U>
    : T;

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

type ExternalState<T> = {
    value: T;
    set: SetState<T>;
};

interface Stringifiable {
    toString: () => string;
}

type FilterItem<T> = {
    label: string;
    value: T;
};

type FilterSnapshot<T> = {
    options: Array<FilterItem<T>>;
    selected: Array<T>;
};

type SearchFilterSnapshot<T> = {
    options: Array<FilterItem<T>>;
    searchType: T;
    stringSearch: string;
};

type OrderFilterSnapshot<T> = {
    options: Array<FilterItem<T>>;
    orderType: T;
};

type Formatter<T, R = string> = (a: T) => R;

type Nullable<T> = T | null;

type Option<T> = T | undefined;

type NullableOption<T> = T | null | undefined;

type OptionalFormatter<T, R = string> = (
    a: Option<T>
) => Option<NonNullable<R>>;

type Indexable = { [name: string]: string };

type Subtract<T extends U, U> = Pick<T, Exclude<keyof T, keyof U>>;

type NonEmptyArray<T> = [...T[], T];

const isNonEmptyArray = <T>(val: T[]): val is NonEmptyArray<T> => {
    if (Array.isArray(val) && val.length > 0) return true;
    return false;
};

const fromOptionToNullable = <T>(value: Option<T>): Nullable<T> =>
    value === undefined ? null : value;

const fromNullableToOption = <T>(value: Nullable<T>): Option<T> =>
    value === null ? undefined : value;

const fromOptionToNullableObject = <U, T extends Record<string, Option<U>>>(
    value: T
): Record<string, Nullable<U>> =>
    Object.entries(value).reduce(
        (acc, [k, v]) => ({
            ...acc,
            [k]: fromOptionToNullable(v),
        }),
        {}
    );

const fromNullableToOptionObject = <U, T extends Record<string, Nullable<U>>>(
    value: T
): Record<string, Option<U>> =>
    Object.entries(value).reduce(
        (acc, [k, v]) => ({
            ...acc,
            [k]: fromNullableToOption(v),
        }),
        {}
    );

const isIndexable = (x: unknown): x is Indexable =>
    typeof x !== 'function' && x instanceof Object && !Array.isArray(x);

const voidFn = (): void => void 0;

const voidPromise = (): Promise<void> => Promise.resolve();

const notNull = <T>(value: T | null): value is T => value !== null;

const notUndefined = <T>(value: T | undefined): value is T =>
    value !== undefined;

type Tuple<T1, T2> = [T1, T2];

const fst = <T1, T2>(tuple: Tuple<T1, T2>): T1 => tuple[0];
const snd = <T1, T2>(tuple: Tuple<T1, T2>): T2 => tuple[1];

type NonNullablePlus<T> = T extends GenericFunction<infer R>
    ? (...args: Parameters<T>) => NonNullable<R>
    : NonNullable<T>;

type WithNonNullable<T, U extends keyof T> = Omit<T, U> & {
    [Key in U]: NonNullablePlus<T[U]>;
};

type Range = { min?: number; max?: number };

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };

type XOR<T, U> = T | U extends object
    ? (Without<T, U> & U) | (Without<U, T> & T)
    : T | U;

type UnionToIntersection<U> = (U extends any ? (x: U) => void : never) extends (
    x: infer I
) => void
    ? I
    : never;

export type {
    EnumMap,
    ExternalState,
    FilterItem,
    FilterSnapshot,
    Formatter,
    FullyDefinedObj,
    GenericAsyncFunction,
    GenericFunction,
    Indexable,
    MouseHandler,
    NonEmptyArray,
    Nullable,
    NullableOption,
    Option,
    OptionalFormatter,
    OrderFilterSnapshot,
    PromiseLikeUnwrap,
    PromiseType,
    Range,
    SearchFilterSnapshot,
    SetState,
    Stringifiable,
    Subtract,
    Tuple,
    XOR,
    WithNonNullable,
    UnionToIntersection,
    WithRequired,
};
export {
    isNonEmptyArray,
    fromNullableToOption,
    fromOptionToNullable,
    fromNullableToOptionObject,
    fromOptionToNullableObject,
    fst,
    isIndexable,
    notNull,
    notUndefined,
    snd,
    voidFn,
    voidPromise,
};
